import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { Button } from '../components/Button';
import { Checkbox } from '../components/Checkbox';
import { cn } from '../utils/cn';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { ProtectedLayout } from '../components/ProtectedLayout';

type IntentionState = {
  accepted: boolean;
  heard: boolean;
  valued: boolean;
  inspired: boolean;
  safe: boolean;
  motivated: boolean;
  supported: boolean;
  appreciated: boolean;
  matter: boolean;
  energized: boolean;
  clarity: boolean;
  worthwhile: boolean;
};

export function ImpactIntentions() {
  const navigate = useNavigate();
  const [intentions, setIntentions] = useState<IntentionState>({
    accepted: false,
    heard: false,
    valued: false,
    inspired: false,
    safe: false,
    motivated: false,
    supported: false,
    appreciated: false,
    matter: false,
    energized: false,
    clarity: false,
    worthwhile: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (key: keyof IntentionState) => {
    setIntentions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setError('');
  };

  const selectedCount = Object.values(intentions).filter(Boolean).length;

  const handleNext = async () => {
    if (isSubmitting) return;

    const selectedIntentions = Object.entries(intentions)
      .filter(([_, value]) => value)
      .map(([key]) => key);

    if (selectedIntentions.length === 0) {
      setError('Please select at least one intention');
      return;
    }

    if (selectedIntentions.length > 5) {
      setError('Please select no more than 5 intentions');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        setError('No authenticated user found. Please sign in again.');
        return;
      }

      const { error: insertError } = await supabase
        .from('impact_intentions')
        .insert(
          selectedIntentions.map(intention => ({
            user_id: user.id,
            intention_type: intention
          }))
        );

      if (insertError) throw insertError;

      navigate('/core-word');
    } catch (error: any) {
      console.error('Error saving impact intentions:', error);
      setError(error.message || 'Failed to save your intentions. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ProtectedLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Build Your Custom AI Prompt
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              The first thing we need for your custom AI prompt are your core values. That takes three questions and then we'll look at your goals (aka Lanes) and your success drivers.
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-600">
              <span className="font-semibold">Selected: {selectedCount}/5</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Impact Intention</h2>
              <p className="text-gray-600">
                When you interact with others, how do you want them to feel? Select 1-5 intentions:
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {Object.entries(intentions).map(([key, value]) => (
                <Checkbox
                  key={key}
                  label={key.charAt(0).toUpperCase() + key.slice(1)}
                  checked={value}
                  onChange={() => handleChange(key as keyof IntentionState)}
                />
              ))}
            </div>

            {selectedCount > 0 && selectedCount <= 5 && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900 text-center font-medium">
                  You've selected {selectedCount} intention{selectedCount !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>

          <Button
            onClick={handleNext}
            disabled={isSubmitting || selectedCount === 0 || selectedCount > 5}
            className={cn(
              'w-full',
              (isSubmitting || selectedCount === 0 || selectedCount > 5) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </ProtectedLayout>
  );
}
